import type {
  AiDraft,
  BackupCounts,
  BackupData,
  Cocktail,
  CocktailDetail,
  CocktailFilter,
  CocktailInput,
  CocktailSummary,
  Ingredient,
  IngredientInput,
  Settings,
  TastingDetail,
  TastingInput,
  VersionDetail,
  VersionInput,
} from '@muddle/core';

import { UNAUTHORIZED_EVENT } from './events';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

/** All server access goes through here. Relative URLs only, so it works under Electron, Vite and a real host. */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: init?.body ? { 'content-type': 'application/json', ...init.headers } : init?.headers,
  });
  if (!res.ok) {
    // Session expired or never started (server mode): let the app show the sign-in screen.
    if (res.status === 401 && !path.startsWith('/auth/'))
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    let message = `Request failed (${res.status})`;
    let fields: Record<string, string> = {};
    try {
      const body = (await res.json()) as { error?: string; fields?: Record<string, string> };
      message = body.error ?? message;
      fields = body.fields ?? {};
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(message, res.status, fields);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

function qs(params: Record<string, string | undefined>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) s.set(k, v);
  const str = s.toString();
  return str ? `?${str}` : '';
}

export interface BackupInfo {
  name: string;
  kind: 'auto' | 'manual' | 'prerestore' | 'nightly';
  size: number;
  createdAt: string;
}

export interface AiStatus {
  configured: boolean;
  keySource: 'env' | 'stored' | null;
  canStoreKey: boolean;
  model: string | null;
  models: { id: string; label: string; note: string }[];
}
export interface AiResult {
  drafts: AiDraft[];
  dropped: number;
  reasons: string[];
  searchErrors?: string[];
  /** For each draft: the drink in your library with the same name, if any. */
  matches: ({ id: string; name: string } | null)[];
  model: string;
  usage: { inputTokens: number; outputTokens: number; searches: number; estCostUsd: number };
}

export const api = {
  authSession: () =>
    request<{ authMode: 'none' | 'token' | 'password'; authenticated: boolean }>('/auth/session'),
  login: (password: string) =>
    request<{ authenticated: boolean }>('/auth/login', json('POST', { password })),
  logout: () => request<{ authenticated: boolean }>('/auth/logout', { method: 'POST' }),
  health: () => request<{ ok: boolean; schemaVersion: number }>('/health'),
  getSettings: () => request<Settings>('/settings'),
  updateSettings: (patch: Partial<Settings>) => request<Settings>('/settings', json('PUT', patch)),

  listIngredients: (q?: string) => request<Ingredient[]>(`/ingredients${qs({ q })}`),
  createIngredient: (input: IngredientInput) =>
    request<Ingredient>('/ingredients', json('POST', input)),
  updateIngredient: (id: string, input: IngredientInput) =>
    request<Ingredient>(`/ingredients/${id}`, json('PUT', input)),
  archiveIngredient: (id: string) => request<void>(`/ingredients/${id}`, { method: 'DELETE' }),

  listCocktails: (f: CocktailFilter = {}) =>
    request<CocktailSummary[]>(
      `/cocktails${qs({ q: f.q, status: f.status, tag: f.tag, spirit: f.spirit, archived: f.archived ? 'true' : undefined, minRating: f.minRating ? String(f.minRating) : undefined })}`,
    ),
  listTags: () => request<string[]>('/tags'),
  getCocktail: (id: string) => request<CocktailDetail>(`/cocktails/${id}`),
  createCocktail: (cocktail: CocktailInput, version: VersionInput) =>
    request<CocktailDetail>('/cocktails', json('POST', { cocktail, version })),
  updateCocktail: (id: string, patch: Partial<CocktailInput>) =>
    request<Cocktail>(`/cocktails/${id}`, json('PATCH', patch)),
  archiveCocktail: (id: string) => request<void>(`/cocktails/${id}/archive`, { method: 'POST' }),
  restoreCocktail: (id: string) => request<void>(`/cocktails/${id}/restore`, { method: 'POST' }),
  deleteCocktail: (id: string) =>
    request<void>(`/cocktails/${id}?confirm=true`, { method: 'DELETE' }),

  addVersion: (cocktailId: string, input: VersionInput) =>
    request<VersionDetail>(`/cocktails/${cocktailId}/versions`, json('POST', input)),
  listTastings: (cocktailId: string) =>
    request<TastingDetail[]>(`/cocktails/${cocktailId}/tastings`),
  createTasting: (input: TastingInput) =>
    request<TastingDetail>(`/versions/${input.versionId}/tastings`, json('POST', input)),
  archiveTasting: (id: string) => request<void>(`/tastings/${id}`, { method: 'DELETE' }),

  listBackups: () => request<BackupInfo[]>('/backups'),
  createBackup: () => request<BackupInfo>('/backups', { method: 'POST' }),
  restoreBackup: (name: string) =>
    request<{ counts: BackupCounts }>(`/backups/${encodeURIComponent(name)}/restore`, {
      method: 'POST',
    }),
  importJson: (data: BackupData) =>
    request<{ counts: BackupCounts }>('/import/json', json('POST', data)),
  importDatabase: (file: Blob) =>
    request<{ counts: BackupCounts }>('/import/database', {
      method: 'POST',
      body: file,
      headers: { 'content-type': 'application/octet-stream' },
    }),

  aiStatus: () => request<AiStatus>('/ai/status'),
  aiConfig: (patch: { key?: string; model?: string }) =>
    request<AiStatus>('/ai/config', json('PUT', patch)),
  aiClearKey: () => request<AiStatus>('/ai/key', { method: 'DELETE' }),
  aiRecipes: (request_: string) =>
    request<AiResult>('/ai/recipes', json('POST', { request: request_ })),

  updateVersionInPlace: (versionId: string, input: VersionInput) =>
    request<VersionDetail>(`/versions/${versionId}`, json('PUT', input)),
};
